import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { dbDictionaryApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type {
  TableFilter,
  UpdateTableReviewInput,
  UpdateColumnReviewInput,
  PendingChangesFilter,
  AddEdgeInput,
  UpsertEnumInput,
} from '../types/db-dictionary.js';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書のスキーマ一覧をTanStack Query経由で取得。全スキーマの名前・テーブル数を返す
 * @graph-connects /db-dictionary/schemas [calls] スキーマ一覧取得API
 * @graph-connects dbDictionaryApi.getSchemas [calls] スキーマ一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useSchemas() {
  return useQuery({
    queryKey: queryKeys.dbDictionary.schemas(),
    queryFn: () => dbDictionaryApi.getSchemas(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書のテーブル一覧をTanStack Query経由で取得。スキーマ指定・フィルタ条件でテーブルリストを返す
 * @graph-connects /db-dictionary/schemas/:schema/tables [calls] テーブル一覧取得API
 * @graph-connects dbDictionaryApi.getTables [calls] テーブル一覧APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTables(schema: string, filter?: TableFilter) {
  return useQuery({
    queryKey: queryKeys.dbDictionary.tables(schema, filter),
    queryFn: async () => {
      const res = await dbDictionaryApi.getTables(schema, filter);
      return res;
    },
    enabled: schema.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business DB辞書のテーブル詳細をTanStack Query経由で取得。カラム定義・レビュー状態・AI生成説明を返す
 * @graph-connects /db-dictionary/schemas/:schema/tables/:table [calls] テーブル詳細取得API
 * @graph-connects dbDictionaryApi.getTableDetail [calls] テーブル詳細APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useTableDetail(schema: string, table: string) {
  const { i18n } = useTranslation();
  const locale = (i18n.language ?? 'ja').startsWith('ja') ? 'ja' : 'en';
  return useQuery({
    queryKey: [...queryKeys.dbDictionary.tableDetail(schema, table), locale] as const,
    queryFn: () => dbDictionaryApi.getTableDetail(schema, table, locale),
    enabled: schema.length > 0 && table.length > 0,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business テーブルレビュー更新ミューテーション。isDead/checked等のレビュー情報をPUT更新し、詳細・一覧キャッシュを無効化
 * @graph-connects /db-dictionary/schemas/:schema/tables/:table [calls] テーブルレビュー更新API（PUT）
 * @graph-connects dbDictionaryApi.updateTableReview [calls] テーブルレビュー更新APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateTableReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      schema,
      table,
      data,
    }: {
      schema: string;
      table: string;
      data: UpdateTableReviewInput;
    }) => dbDictionaryApi.updateTableReview(schema, table, data),
    onSuccess: (_data, variables) => {
      // テーブル詳細とテーブル一覧を再取得
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dbDictionary.tableDetail(variables.schema, variables.table),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dbDictionary.all,
      });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business カラムレビュー更新ミューテーション。個別カラムのisDead/checked等をPUT更新し、テーブル詳細キャッシュを無効化
 * @graph-connects /db-dictionary/schemas/:schema/tables/:table/columns/:column [calls] カラムレビュー更新API（PUT）
 * @graph-connects dbDictionaryApi.updateColumnReview [calls] カラムレビュー更新APIクライアント呼び出し
 * @graph-connects queryKeys [calls] TanStack Queryキー生成
 */
export function useUpdateColumnReview() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      schema,
      table,
      column,
      data,
    }: {
      schema: string;
      table: string;
      column: string;
      data: UpdateColumnReviewInput;
    }) => dbDictionaryApi.updateColumnReview(schema, table, column, data),
    onSuccess: (_data, variables) => {
      // テーブル詳細を再取得
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dbDictionary.tableDetail(variables.schema, variables.table),
      });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business MCP db-graph-serverの隣接テーブルキャッシュを手動削除するミューテーション
 * @graph-connects /db-dictionary/cache/invalidate [calls] キャッシュ削除API（POST）
 * @graph-connects dbDictionaryApi.invalidateCache [calls] キャッシュ削除APIクライアント呼び出し
 */
export function useInvalidateCache() {
  return useMutation({
    mutationFn: () => dbDictionaryApi.invalidateCache(),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 差分レビューの変更テーブル一覧をTanStack Query経由で取得
 * @graph-connects /db-dictionary/changes [calls] 変更一覧取得API
 * @graph-connects dbDictionaryApi.getPendingChanges [calls] 変更一覧APIクライアント呼び出し
 */
export function usePendingChanges(filter?: PendingChangesFilter) {
  return useQuery({
    queryKey: [...queryKeys.dbDictionary.all, 'pendingChanges', filter],
    queryFn: () => dbDictionaryApi.getPendingChanges(filter),
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 変更テーブルを確認済みにマークするミューテーション
 * @graph-connects /db-dictionary/changes/:qualifiedName [calls] 変更確認API（PUT）
 * @graph-connects dbDictionaryApi.reviewChange [calls] 変更確認APIクライアント呼び出し
 */
export function useReviewChange() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (qualifiedName: string) => dbDictionaryApi.reviewChange(qualifiedName),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...queryKeys.dbDictionary.all, 'pendingChanges'],
      });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エッジ追加ミューテーション。FK/SAME_ENTITY/USES_ENUM 追加後、テーブル詳細キャッシュを無効化
 * @graph-connects /db-dictionary/edges [calls] エッジ追加API (POST)
 * @graph-connects dbDictionaryApi.addEdge [calls] エッジ追加APIクライアント
 */
export function useAddEdge(schema: string, table: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddEdgeInput) => dbDictionaryApi.addEdge(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dbDictionary.tableDetail(schema, table),
      });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business エッジ削除ミューテーション。テーブル詳細キャッシュを無効化
 * @graph-connects /db-dictionary/edges/:edgeId [calls] エッジ削除API (DELETE)
 * @graph-connects dbDictionaryApi.deleteEdge [calls] エッジ削除APIクライアント
 */
export function useDeleteEdge(schema: string, table: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (edgeId: string) => dbDictionaryApi.deleteEdge(edgeId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.dbDictionary.tableDetail(schema, table),
      });
    },
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Enum 詳細取得フック。クリックで開いた enum popup でのみ enabled
 * @graph-connects /db-dictionary/enums/:name [calls] Enum 詳細取得API
 * @graph-connects dbDictionaryApi.getEnumDetail [calls] Enum 詳細APIクライアント
 */
export function useEnumDetail(enumName: string | null) {
  return useQuery({
    queryKey: ['dbDictionary', 'enum', enumName],
    queryFn: () => {
      if (!enumName) throw new Error('enumName required');
      return dbDictionaryApi.getEnumDetail(enumName);
    },
    enabled: !!enumName,
  });
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business Enum upsert ミューテーション。enum 詳細キャッシュと関連テーブル詳細を無効化
 * @graph-connects /db-dictionary/enums/:name [calls] Enum upsert API (PUT)
 * @graph-connects dbDictionaryApi.upsertEnum [calls] Enum upsert APIクライアント
 */
export function useUpsertEnum(invalidateTable?: { schema: string; table: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpsertEnumInput) => dbDictionaryApi.upsertEnum(input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ['dbDictionary', 'enum', variables.name],
      });
      if (invalidateTable) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.dbDictionary.tableDetail(
            invalidateTable.schema,
            invalidateTable.table,
          ),
        });
      }
    },
  });
}
